import { z } from "zod";

import { MAX_PASSWORD_LENGTH,MIN_PASSWORD_LENGTH } from "../constants/password.js";
import { SUBSCRIPTION_STATUS } from "../constants/subscriptionStatus.js";

const subscriptionStatusEnum = z.enum([
    SUBSCRIPTION_STATUS.NO_ACCESS,
    SUBSCRIPTION_STATUS.PENDING,
    SUBSCRIPTION_STATUS.ACTIVE,
    SUBSCRIPTION_STATUS.EXPIRED,
]);

const invitationReminderSchema = z.object({
    stage: z.enum(["D3", "D6", "D9"]),
    sent_at: z.string(),
});

export const UserResponseSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.email(),
    role: z.string(),
    is_admin: z.boolean(),
    admin_role: z.string().nullable(),
    licence_id: z.uuid(),
    licence_name: z.string().nullable(),
    subscription_date: z.string(),
    subscription_status: subscriptionStatusEnum,
    last_login_date: z.string().nullable(),
    archived_at: z.string().nullable(),
    /** The user's LinkedIn member profile, normalised; null when none was given (#591). */
    linkedin_url: z.string().nullable(),
    /** Profile photo (#579): Invyo staff only, null when none was uploaded. */
    avatar_url: z.string().nullable(),
    invitation_reminders: z.array(invitationReminderSchema),
    /** When the latest invitation link was sent; null if none ever was. */
    invitation_sent_at: z.string().nullable(),
    /** When that link stops working (12 days after it was sent). */
    invitation_expires_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
export type UserResponse = z.infer<typeof UserResponseSchema>;

export const CreateUserRequestSchema = z.object({
    first_name: z.string().min(1),
    last_name: z.string().min(1),
    email: z.email(),
    password: z.string().min(MIN_PASSWORD_LENGTH).max(MAX_PASSWORD_LENGTH).optional(),
    role: z.string().optional().default("Standard"),
    is_admin: z.boolean().optional().default(false),
    licence_id: z.uuid().optional(),
    /** Optional LinkedIn member profile URL; empty or null = none. Normalised and checked server-side. */
    linkedin_url: z.string().nullable().optional(),
});
export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>;

export const UpdateUserRequestSchema = z.object({
    first_name: z.string().min(1).optional(),
    last_name: z.string().min(1).optional(),
    email: z.email().optional(),
    role: z.string().optional(),
    is_admin: z.boolean().optional(),
    admin_role: z.string().optional(),
    subscription_status: subscriptionStatusEnum.optional(),
    /** Set, replace or (with "" / null) clear the LinkedIn member profile URL. */
    linkedin_url: z.string().nullable().optional(),
});
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;

export const UserListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(UserResponseSchema),
});
export type UserListResponse = z.infer<typeof UserListResponseSchema>;

export const UserDetailResponseSchema = z.object({
    success: z.boolean(),
    data: UserResponseSchema,
});
export type UserDetailResponse = z.infer<typeof UserDetailResponseSchema>;

// GET /users/:id/deletion-impact: what a deletion takes with the account (#587)
export const UserDeletionImpactSchema = z.object({
    comments: z.number().int(),
    api_keys: z.number().int(),
});
export type UserDeletionImpact = z.infer<typeof UserDeletionImpactSchema>;

export const UserDeletionImpactResponseSchema = z.object({
    success: z.boolean(),
    data: UserDeletionImpactSchema,
});
